import { useState, useEffect } from 'react';
import { SCHEDULER_BACKEND } from '../constants';
import { platformStyle } from '../platformStyle';
import { summarizeHorizon, formatDay, describeAhead } from '../pipelineFormat';

// The first thing on the Calendar: what is scheduled, which platforms, when, and how
// far ahead it reaches. Looks 60 days out (the calendar service refuses ranges over
// 62 days), independent of the month shown below.
const AHEAD_DAYS = 60;
const DAY = 86400000;

async function fetchUpcoming() {
  const start = new Date();
  const end = new Date(start.getTime() + AHEAD_DAYS * 86400000);
  const res = await fetch(`${SCHEDULER_BACKEND}/calendar/posts?start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to load the schedule');
  return data;
}

export default function ScheduleHorizon({ load = fetchUpcoming, now = () => Date.now() }) {
  const [state, setState] = useState({ loading: true, posts: [], errors: {}, failed: null });

  useEffect(() => {
    let alive = true;
    load().then((data) => { if (alive) setState({ loading: false, posts: data.posts || [], errors: data.sourceErrors || {}, failed: null }); })
      .catch((e) => { if (alive) setState({ loading: false, posts: [], errors: {}, failed: e.message }); });
    return () => { alive = false; };
  }, [load]);

  const box = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px', marginBottom: 14 };
  if (state.loading) return <div style={{ ...box, fontSize: 13, color: '#64748b' }}>Checking what is scheduled{'…'}</div>;
  if (state.failed) return <div role="alert" style={{ ...box, borderColor: '#fca5a5', background: '#fef2f2', fontSize: 13, color: '#991b1b' }}>Could not load the schedule: {state.failed}</div>;

  const h = summarizeHorizon(state.posts, now());
  // If the last post is at the edge of what we looked at, it probably goes further: say "at least".
  const reachesEdge = h.lastAt && new Date(h.lastAt).getTime() >= now() + (AHEAD_DAYS - 3) * DAY;
  // A source that failed means the picture may be incomplete; say so instead of showing a confident number.
  const missing = Object.entries(state.errors).filter(([, msg]) => msg).map(([src]) => src);

  if (h.count === 0) {
    return (
      <div style={{ ...box, background: '#fffbeb', borderColor: '#f5d78e' }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#92400e' }}>Nothing is scheduled ahead</div>
        <div style={{ fontSize: 13, color: '#78350f', marginTop: 4 }}>The calendar is empty from today on. Pick an idea on Repurpose and click Generate All to fill it.</div>
        {missing.length > 0 && <div style={{ fontSize: 12, color: '#78350f', marginTop: 6 }}>Note: {missing.join(', ')} did not load, so this may be incomplete.</div>}
      </div>
    );
  }
  return (
    <div style={box} aria-label="Scheduled ahead">
      <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
        {h.count} post{h.count === 1 ? '' : 's'} scheduled through {formatDay(h.lastAt)} <span style={{ color: '#16a34a' }}>({reachesEdge ? `at least ${AHEAD_DAYS} days ahead` : describeAhead(h.lastAt, now())})</span>
      </div>
      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Next post: {formatDay(h.firstAt)}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
        {h.platforms.map((p) => {
          const st = platformStyle(p.platform);
          return (
            <span key={p.platform} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, border: '1px solid #e2e8f0', fontSize: 12, color: '#0f172a' }}>
              <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: st.color }} />
              <strong>{st.label}</strong>
              <span style={{ color: '#64748b' }}>next {formatDay(p.nextAt)} {'·'} {p.count} queued</span>
            </span>
          );
        })}
      </div>
      {missing.length > 0 && <div style={{ fontSize: 12, color: '#92400e', marginTop: 8 }}>{missing.join(', ')} did not load, so this may be incomplete.</div>}
    </div>
  );
}
